import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["scroller", "list", "template"];
  static values = {
    mode: { type: String, default: "client" },
    markers: Array
  };

  connect() {
    this._allValuers = (this.markersValue || []).filter(
      (m) => (m.kind || "").toLowerCase() === "valuers"
    );
    this._selectedIds = new Set();
    this._pendingRevisedId = null;
    this._pendingRevisedItem = null;
    this._scrollEndTimeout = null;
    this._isWheelScrolling = false;
    this._originalScrollSnapType = undefined;

    // Horizontal scroll with mouse wheel
    // Listen on the main controller element to catch all wheel events over cards
    this._onWheel = (e) => this._handleWheelScroll(e);
    this.element.addEventListener("wheel", this._onWheel, { passive: false });

    // Reveal the scrollbar only while the list is actively scrolling (wheel or drag)
    this._isDragging = false;
    this._isPointerDown = false;
    this._dragPointerId = null;
    this._dragThresholdPx = 6;

    if (this.hasScrollerTarget) {
      this._onScroll = () => this._handleScrollActivity();
      this.scrollerTarget.addEventListener("scroll", this._onScroll, { passive: true });

      this._onPointerDown = (e) => this._onDragPointerDown(e);
      this._onPointerMove = (e) => this._onDragPointerMove(e);
      this._onPointerUp = (e) => this._onDragPointerUp(e);
      this.scrollerTarget.addEventListener("pointerdown", this._onPointerDown, { passive: true });
      window.addEventListener("pointermove", this._onPointerMove, { passive: false });
      window.addEventListener("pointerup", this._onPointerUp, { passive: true });
      window.addEventListener("pointercancel", this._onPointerUp, { passive: true });

      // Native browser drag-and-drop on <a>/<img> overlays interrupts pointermove tracking
      this._onNativeDragStart = (e) => e.preventDefault();
      this.scrollerTarget.addEventListener("dragstart", this._onNativeDragStart);

      this._onResize = () => this._updateScrollFade();
      window.addEventListener("resize", this._onResize);
    }

    // map -> scroll to card
    this._onSelectFromMap = (e) => this._handleSelectFromMap(e.detail);
    window.addEventListener("map:selectValuer", this._onSelectFromMap);

    // sidebar sync
    this._onSelSet = (e) => this._syncSelectedFrom(e.detail || []);
    this._onSelAdd = (e) => this._markSelected(e.detail?.id, true);
    this._onSelRem = (e) => {
      const id = e.detail?.id;
      if (id != null) {
        this._disableValuerFormFields(id);
        this._markSelected(id, false);
        if (String(this._pendingRevisedId) === String(id)) {
          this._pendingRevisedId = null;
          this._pendingRevisedItem = null;
          this._pendingCard = null;
        }
      }
    };
    window.addEventListener("valuersSelection:set", this._onSelSet);
    window.addEventListener("valuersSelection:add", this._onSelAdd);
    window.addEventListener("valuersSelection:remove", this._onSelRem);

    // Revised fixed fee -> finish select after Save
    this._onRevisedConfirm = async (e) => {
      const id = this._pendingRevisedId;
      const item = this._pendingRevisedItem;
      const card = this._pendingCard;

      if (!id || !item || !card) return;

      const amount = e.detail?.amount || "";

      const isRisky = card.dataset.riskyValuer === 'true';

      if (isRisky) {
        const riskyReason = card.dataset.riskyReason || 'Are you sure?';
        const minFeeLevelStr = card.dataset.minFeeLevel || '';

        const isMinFeeReason = riskyReason === 'Min Fee Not Met';

        let shouldConfirm = true;
        if (isMinFeeReason && minFeeLevelStr && amount) {
          const minFeeLevel = parseFloat(minFeeLevelStr);
          const revisedAmount = parseFloat(amount);

          // Skip confirmation if Min Fee Not Met is no longer true
          if (!isNaN(minFeeLevel) && !isNaN(revisedAmount) && revisedAmount >= minFeeLevel) {
            shouldConfirm = false;
          }
        }

        if (shouldConfirm) {
          const confirmElement = document.querySelector('[data-controller~="confirm"]');
          const confirmController = this.application.getControllerForElementAndIdentifier(
            confirmElement,
            'confirm'
          );

          if (confirmController) {
            const confirmed = await confirmController.request({
              title: `${riskyReason}!`,
              message: 'Are you sure you want to proceed with this Valuer?'
            });

            if (!confirmed) {
              this._pendingRevisedId = null;
              this._pendingRevisedItem = null;
              this._pendingCard = null;
              return;
            }
          }
        }
      }

      this._pendingRevisedId = null;
      this._pendingRevisedItem = null;
      this._pendingCard = null;

      const feeTypeAttribute = this._getFeeTypeAttribute("revised_fixed_fee");
      const isNonFixedFeeOpportunity = card.dataset.nonFixedFeeOpportunity === 'true';
      const valuationFirmName = card.querySelector('[data-role="quote-type-badge"]')?.dataset.valuationFirmName || '';

      let badgeLabel = null;
      if (isNonFixedFeeOpportunity) {
        const firstName = valuationFirmName.split(' ')[0];
        badgeLabel = `Fixed Fee - ${firstName}`;
      }

      const inputs = card.querySelectorAll('input[type="hidden"]');
      inputs.forEach(input => {
        input.disabled = false;
        if (input.name.includes('[fee_type]')) {
          input.value = 'revised_fixed_fee';
        }
        if (input.name.includes('[amount]')) {
          input.value = amount;
        }
      });

      this._emit("valuersSelection:add", {
        id: item.id,
        title: item.title,
        badges: [feeTypeAttribute],
        badgeLabel: badgeLabel,
        feeType: feeTypeAttribute,
        amount: amount,
        distance: card.dataset.distance,
        contactsUrl: card.dataset.contactsUrl,
      });

      this._markSelected(item.id, true);
    };
    window.addEventListener("revised-fixed-fee:confirm", this._onRevisedConfirm);

    this._onRevisedCancel = () => {
      this._pendingRevisedId = null;
      this._pendingRevisedItem = null;
      this._pendingCard = null;
    };
    window.addEventListener("revised-fixed-fee:cancel", this._onRevisedCancel);

    this._onMenuChange = (e) => {
      const card = e.target.closest("[data-card]");
      if (!card) return;
      // expect e.detail.value = "fixed_fee" | "quote_request" | "revised_fixed_fee"
      const value = e.detail?.value || "fixed_fee";
      card.dataset.quoteType = value;

      // Toggle button visibility based on quote type
      const regularBtn = card.querySelector('[data-role="select-btn"]');
      const revisedBtn = card.querySelector('[data-role="select-btn-revised"]');
      if (value === "revised_fixed_fee") {
        if (regularBtn) regularBtn.classList.add('hidden');
        if (revisedBtn) revisedBtn.classList.remove('hidden');
      } else {
        if (regularBtn) regularBtn.classList.remove('hidden');
        if (revisedBtn) revisedBtn.classList.add('hidden');
      }
    };
    this.element.addEventListener("menu:change", this._onMenuChange, true);

    this._renderFullList(this._allValuers);

    if (this.hasScrollerTarget) {
      requestAnimationFrame(() => this._updateScrollFade());
    }
  }

  disconnect() {
    if (this._onWheel) {
      this.element.removeEventListener("wheel", this._onWheel);
    }
    // Clear snap timeout
    if (this._snapTimeout) {
      clearTimeout(this._snapTimeout);
    }
    // Clear scroll end timeout
    if (this._scrollEndTimeout) {
      clearTimeout(this._scrollEndTimeout);
    }
    // Clear scrollbar-reveal timeout
    if (this._scrollbarHideTimeout) {
      clearTimeout(this._scrollbarHideTimeout);
    }
    if (this.hasScrollerTarget) {
      this.scrollerTarget.removeEventListener("scroll", this._onScroll);
      this.scrollerTarget.removeEventListener("pointerdown", this._onPointerDown);
      this.scrollerTarget.removeEventListener("dragstart", this._onNativeDragStart);
    }
    window.removeEventListener("pointermove", this._onPointerMove);
    window.removeEventListener("pointerup", this._onPointerUp);
    window.removeEventListener("pointercancel", this._onPointerUp);
    window.removeEventListener("resize", this._onResize);
    window.removeEventListener("map:selectValuer", this._onSelectFromMap);
    window.removeEventListener("valuersSelection:set", this._onSelSet);
    window.removeEventListener("valuersSelection:add", this._onSelAdd);
    window.removeEventListener("valuersSelection:remove", this._onSelRem);
    window.removeEventListener("revised-fixed-fee:confirm", this._onRevisedConfirm);
    window.removeEventListener("revised-fixed-fee:cancel", this._onRevisedCancel);
    this.element.removeEventListener("menu:change", this._onMenuChange, true);
  }

  // Reveals the scrollbar thumb while scrolling (wheel or drag), hides it once idle
  _handleScrollActivity() {
    if (!this.hasScrollerTarget) return;

    this.scrollerTarget.classList.add("is-scrolling");
    clearTimeout(this._scrollbarHideTimeout);
    this._scrollbarHideTimeout = setTimeout(() => {
      this.scrollerTarget.classList.remove("is-scrolling");
    }, 600);

    this._updateScrollFade();
  }

  // Fades the top/bottom edges of the card list to signal more content off-screen,
  // via mask-image rather than a background overlay since the panel floats over the map
  _updateScrollFade() {
    if (!this.hasScrollerTarget) return;

    const el = this.scrollerTarget;
    const hasOverflow = el.scrollHeight > el.clientHeight + 1;
    const atTop = el.scrollTop <= 4;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 4;

    const fadeTop = hasOverflow && !atTop;
    const fadeBottom = hasOverflow && !atBottom;

    let mask = "none";
    if (fadeTop && fadeBottom) {
      mask = "linear-gradient(to bottom, transparent 0, black 32px, black calc(100% - 32px), transparent 100%)";
    } else if (fadeBottom) {
      mask = "linear-gradient(to bottom, black calc(100% - 32px), transparent 100%)";
    } else if (fadeTop) {
      mask = "linear-gradient(to bottom, transparent 0, black 32px, black 100%)";
    }

    el.style.maskImage = mask;
    el.style.webkitMaskImage = mask;
  }

  // Click-and-drag scrolling on the card list background
  _onDragPointerDown(e) {
    if (e.button !== 0) return;
    if (e.target.closest("[data-prevent-card-nav], button, a, input, select, textarea, label")) return;

    this._isPointerDown = true;
    this._isDragging = false;
    this._dragPointerId = e.pointerId;
    this._dragStartY = e.clientY;
    this._dragStartScrollTop = this.scrollerTarget.scrollTop;
  }

  _onDragPointerMove(e) {
    if (!this._isPointerDown) return;
    if (this._dragPointerId != null && e.pointerId !== this._dragPointerId) return;

    const dy = e.clientY - this._dragStartY;

    if (!this._isDragging && Math.abs(dy) >= this._dragThresholdPx) {
      this._isDragging = true;

      if (this._originalScrollSnapType === undefined) {
        this._originalScrollSnapType = this.scrollerTarget.style.scrollSnapType;
      }
      this.scrollerTarget.style.scrollSnapType = "none";
      this.scrollerTarget.classList.add("cursor-grabbing");

      try {
        this.scrollerTarget.setPointerCapture(e.pointerId);
      } catch (_) {}
    }

    if (!this._isDragging) return;

    e.preventDefault();
    this.scrollerTarget.scrollTop = this._dragStartScrollTop - dy;
  }

  _onDragPointerUp(e) {
    if (!this._isPointerDown) return;
    if (this._dragPointerId != null && e.pointerId !== this._dragPointerId) return;

    this._isPointerDown = false;
    this.scrollerTarget.classList.remove("cursor-grabbing");

    try {
      this.scrollerTarget.releasePointerCapture(e.pointerId);
    } catch (_) {}

    if (this._isDragging && this._originalScrollSnapType !== undefined) {
      this.scrollerTarget.style.scrollSnapType = this._originalScrollSnapType;
      this._originalScrollSnapType = undefined;
    }

    // Defer clearing the drag flag so the click that follows pointerup
    // (navigate()) still sees it and gets suppressed.
    if (this._isDragging) {
      requestAnimationFrame(() => { this._isDragging = false; });
    }

    this._dragPointerId = null;
  }

  // Map -> scroll
  _handleSelectFromMap(selected) {
    if (!selected || (selected.kind || "").toLowerCase() !== "valuers") return;
    const key = this._key(selected);
    const card = this.listTarget.querySelector(`[data-card][data-key="${CSS.escape(key)}"]`);
    if (card) card.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }

  // Wheel scroll handler for vertical scrolling
  _handleWheelScroll(event) {
    // Make sure scrollerTarget exists
    if (!this.hasScrollerTarget) {
      return;
    }

    // Check if scroller can actually scroll vertically
    const scroller = this.scrollerTarget;
    const hasOverflow = scroller.scrollHeight > scroller.clientHeight;

    if (!hasOverflow) {
      return;
    }

    // This prevents page scroll and allows the cards container to scroll vertically
    if (event.deltaY !== 0) {
      const canScrollUp = scroller.scrollTop > 0;
      const canScrollDown = scroller.scrollTop < (scroller.scrollHeight - scroller.clientHeight - 1);

      const scrollingUp = event.deltaY < 0;
      const scrollingDown = event.deltaY > 0;

      // Only prevent default if we can actually scroll in that direction
      if ((scrollingUp && canScrollUp) || (scrollingDown && canScrollDown)) {
        event.preventDefault();
        event.stopPropagation();

        // Disable scroll snap on first wheel event
        if (!this._isWheelScrolling) {
          this._isWheelScrolling = true;
          this._originalScrollSnapType = scroller.style.scrollSnapType;
          scroller.style.scrollSnapType = 'none';
        }

        // Scroll vertically
        scroller.scrollBy({
          top: event.deltaY,
          behavior: 'auto'
        });

        // Detect when scrolling has actually stopped
        clearTimeout(this._scrollEndTimeout);
        this._scrollEndTimeout = setTimeout(() => {
          this._isWheelScrolling = false;
          if (this._originalScrollSnapType !== undefined) {
            scroller.style.scrollSnapType = this._originalScrollSnapType;
            this._originalScrollSnapType = undefined;
          }
        }, 150);
      } else if (hasOverflow) {
        // At the edge but have overflow - still prevent page scroll
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  _renderFullList(items) {
    const list = this.listTarget;
    const existingCards = list.querySelectorAll("[data-card]");

    this._itemsByKey = new Map();

    if (existingCards.length > 0) {
      // Server-rendered cards exist - use them
      const src = Array.isArray(items) ? items.slice() : [];

      existingCards.forEach((card) => {
        const cardId = card.dataset.id;
        const key = card.dataset.key || cardId;
        let item = src.find(i => String(i.id) === cardId);

        // If no item from markers, create a synthetic one from card data
        if (!item && cardId) {
          item = {
            id: cardId,
            title: card.querySelector('h2')?.textContent?.trim() || 'Valuer',
            kind: 'valuers'
          };
        }

        if (item && key) {
          this._itemsByKey.set(key, item);
          if (!card.dataset.quoteType) {
            card.dataset.quoteType = "fixed_fee";
          }
          // Add hover listeners for marker animation
          card.addEventListener('mouseenter', () => this._handleCardHover(item, true));
          card.addEventListener('mouseleave', () => this._handleCardHover(item, false));
          this._syncCardSelectButton(card, item);
        }
      });
    } else {
      // Fallback to template rendering if needed
      if (!this.hasTemplateTarget) return;

      list.innerHTML = "";
      const src = Array.isArray(items) ? items.slice() : [];
      while (src.length < 3) src.push({ placeholder: true, title: "No valuer", subtitle: "—" });

      src.forEach((item, idx) => {
        const node = this.templateTarget.content.firstElementChild.cloneNode(true);
        const key = this._key(item) || `placeholder-${idx}`;
        node.dataset.key = key;
        if (item.id != null) node.dataset.id = String(item.id);

        node.dataset.quoteType = node.dataset.quoteType || "fixed_fee";

        this._itemsByKey.set(key, item);

        const titleEl = node.querySelector("[data-field='title']");
        if (titleEl) titleEl.textContent = item.title || "Valuer";
        const subtitleEl = node.querySelector("[data-field='subtitle']");
        if (subtitleEl) subtitleEl.textContent = item.subtitle || "";

        // Add hover listeners for marker animation
        node.addEventListener('mouseenter', () => this._handleCardHover(item, true));
        node.addEventListener('mouseleave', () => this._handleCardHover(item, false));
        this._syncCardSelectButton(node, item);
        list.appendChild(node);
      });
    }
  }

  _handleCardHover(item, isEntering) {
    if (!item || item.placeholder || (item.kind || "").toLowerCase() !== "valuers") return;

    if (isEntering) {
      this._emit("valuersCards:hoverValuerEnter", item);
    } else {
      this._emit("valuersCards:hoverValuerLeave", item);
    }
  }

  // === Actions (card) ===
  navigate(event) {
    if (this._isDragging) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    if (this._isClickInsideInteractive(event)) return;

    const card = event.currentTarget.closest("[data-card]");
    if (!card) return;

    const key = card.dataset.key;
    const item = this._itemsByKey?.get(key);
    if (!item || item.placeholder) return;

    this._emit("valuersCards:focusValuer", item);
  }

  _isClickInsideInteractive(event) {
    const t = event.target;

    if (t.closest("[data-prevent-card-nav='true'], [data-prevent-card-nav]")) return true;

    if (t.closest("button, a, input, select, textarea, label, [role='button'], [contenteditable], [contenteditable='true']")) {
      return true;
    }

    const actionEl = t.closest("[data-action]");
    if (actionEl && actionEl !== event.currentTarget) {
      return true;
    }

    return false;
  }

  openRevisedFeeModal(event) {
    event.stopPropagation();
    event.preventDefault();

    const card = event.currentTarget.closest("[data-card]");
    if (!card) return;

    const key = card.dataset.key;
    const item = this._itemsByKey?.get(key);
    if (!item || item.placeholder || item.id == null) return;

    if (!this._isValidSelection(item, item.id)) return;

    this._pendingRevisedId = item.id;
    this._pendingRevisedItem = item;
    this._pendingCard = card;

    const modal = document.getElementById('revised-fixed-fee-modal');
    if (modal) {
      const mainElement = document.querySelector('main');
      if (mainElement && !mainElement.contains(modal)) {
        mainElement.appendChild(modal);
      }

      modal.classList.remove('hidden');
      modal.classList.add('flex');
    }
  }

  async selectWithConfirm(event) {
    event.stopPropagation();

    const card = event.currentTarget.closest("[data-card]");

    if (!card) {
      return;
    }

    const confirmElement = document.querySelector('[data-controller~="confirm"]');

    const confirmController = this.application.getControllerForElementAndIdentifier(
      confirmElement,
      'confirm'
    );

    if (!confirmController) {
      this._performSelect(card);
      return;
    }

    const riskyReason = card.dataset.riskyReason || 'Are you sure?';
    const confirmed = await confirmController.request({
      title: `${riskyReason}!`,
      message: 'Are you sure you want to proceed with this Valuer?'
    });

    if (confirmed) {
      this._performSelect(card);
    }
  }

  select(event) {
    event.stopPropagation();

    const card = event.currentTarget.closest("[data-card]");
    if (!card) {
      return;
    }

    this._performSelect(card);
  }

  _performSelect(card) {
    const key = card.dataset.key;
    const item = this._itemsByKey?.get(key);
    const id = item?.id;

    if (!this._isValidSelection(item, id)) {
      return;
    }

    const quoteType = card.dataset.quoteType || "fixed_fee";
    this._enableValuerFormFields(card, quoteType);
    this._emitValuerSelection(item, card, quoteType);
    this._markSelected(id, true);
  }

  _isValidSelection(item, id) {
    if (!item || item.placeholder || id == null) {
      return false;
    }
    return !this._selectedIds.has(String(id));
  }

  _enableValuerFormFields(card, quoteType) {
    const inputs = card.querySelectorAll('input[type="hidden"]');
    inputs.forEach(input => {
      input.disabled = false;
      if (input.name.includes('[fee_type]')) {
        input.value = quoteType;
      }
      if (input.name.includes('[amount]')) {
        input.value = this._calculateAmount(quoteType, card);
      }
    });
  }

  _emitValuerSelection(item, card, quoteType) {
    const feeTypeAttribute = this._getFeeTypeAttribute(quoteType);
    const isNonFixedFeeOpportunity = card.dataset.nonFixedFeeOpportunity === 'true';
    const valuationFirmName = card.querySelector('[data-role="quote-type-badge"]')?.dataset.valuationFirmName || '';

    let badgeLabel = null;
    if (isNonFixedFeeOpportunity) {
      const firstName = valuationFirmName.split(' ')[0];
      badgeLabel = `Fixed Fee - ${firstName}`;
    }

    this._emit("valuersSelection:add", {
      id: item.id,
      title: item.title,
      badges: [feeTypeAttribute],
      badgeLabel: badgeLabel,
      feeType: feeTypeAttribute,
      amount: this._calculateAmount(quoteType, card),
      distance: card.dataset.distance,
      contactsUrl: card.dataset.contactsUrl,
    });
  }

  // === Sync selection (sidebar) ===
  _syncSelectedFrom(list) {
    this._selectedIds = new Set(
      (list || []).map(v => v && v.id).filter(id => id != null).map(id => String(id))
    );
    this.listTarget.querySelectorAll("[data-card]").forEach((card) => {
      const key = card.dataset.key;
      const item = this._itemsByKey?.get(key);
      if (item) this._syncCardSelectButton(card, item);
    });
  }

  _markSelected(id, on) {
    if (id == null) return;
    const sid = String(id);
    if (on) this._selectedIds.add(sid); else this._selectedIds.delete(sid);
    const card = this.listTarget.querySelector(`[data-card][data-id="${CSS.escape(sid)}"]`);
    if (card) {
      const key = card.dataset.key;
      const item = this._itemsByKey?.get(key);
      if (item) this._syncCardSelectButton(card, item);
    }
  }

  _syncCardSelectButton(cardEl, item) {
    const primaryBtn  = cardEl.querySelector('[data-role="select-btn"][data-variant="primary"]');
    const selectedBtn = cardEl.querySelector('[data-role="select-btn"][data-variant="selected"]');
    const revisedBtn = cardEl.querySelector('[data-role="select-btn-revised"]');
    const dropdownBtn = cardEl.querySelector('[data-menu-target="button"]');
    const quoteBadge = cardEl.querySelector('[data-role="quote-type-badge"]');
    const quoteBadgeText = cardEl.querySelector('[data-role="quote-type-badge-text"]');

    const selected = item.id != null && this._selectedIds.has(String(item.id));
    const quoteType = cardEl.dataset.quoteType || "fixed_fee";

    if (selected) {
      primaryBtn.classList.add("hidden");
      if (revisedBtn) revisedBtn.classList.add("hidden");
      selectedBtn.classList.remove("hidden");
      selectedBtn.disabled = true;
      if (dropdownBtn) {
        dropdownBtn.disabled = true;
        dropdownBtn.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
      }
      if (quoteBadge && quoteBadgeText) {
        const badgeText = this._getQuoteTypeBadgeText(cardEl);
        quoteBadgeText.textContent = badgeText;
        quoteBadge.classList.remove('hidden');
      }
    } else {
      selectedBtn.classList.add("hidden");
      if (quoteType === "revised_fixed_fee") {
        primaryBtn.classList.add("hidden");
        if (revisedBtn) revisedBtn.classList.remove("hidden");
      } else {
        primaryBtn.classList.remove("hidden");
        if (revisedBtn) revisedBtn.classList.add("hidden");
      }
      if (dropdownBtn) {
        dropdownBtn.disabled = false;
        dropdownBtn.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
      }
      if (quoteBadge) {
        quoteBadge.classList.add('hidden');
      }
    }
  }

  _getQuoteTypeBadgeText(card) {
    const quoteType = card.dataset.quoteType || 'fixed_fee';
    const isNonFixedFeeOpportunity = card.dataset.nonFixedFeeOpportunity === 'true';
    const valuationFirmName = card.querySelector('[data-role="quote-type-badge"]')?.dataset.valuationFirmName || '';

    // For Gateway/Pinnacle (fixed_fee_opportunity_enabled: false), always show "Fixed Fee - [Name]"
    if (isNonFixedFeeOpportunity) {
      const firstName = valuationFirmName.split(' ')[0];
      return `Fixed Fee - ${firstName}`;
    }

    switch (quoteType) {
      case 'fixed_fee':
        return 'Fixed Fee';
      case 'revised_fixed_fee':
        return 'Revised Fixed Fee';
      case 'quote_request':
        return 'Quote Request';
      default:
        return 'Quote Request';
    }
  }

  _findById(id) {
    const sid = String(id);
    return this._allValuers.find(v => String(v.id) === sid);
  }

  // === Utils ===
  _calculateAmount(quoteType, card) {
    if (quoteType === "fixed_fee") {
      return card.dataset.fixedFeeAmount || "";
    }
    return "";
  }

  _getFeeTypeAttribute(quoteType) {
    switch (quoteType) {
      case "fixed_fee":
        return "fixed_fee";
      case "revised_fixed_fee":
        return "revised_fixed_fee";
      case "quote_request":
        return "quote_request";
      default:
        return "quote_request";
    }
  }

  _disableValuerFormFields(valuationFirmId) {
    const sid = String(valuationFirmId);
    const card = this.listTarget.querySelector(`[data-card][data-id="${CSS.escape(sid)}"]`);
    if (!card) return;

    const inputs = card.querySelectorAll('input[type="hidden"]');
    inputs.forEach(input => input.disabled = true);
  }

  _key(obj) { return obj?.id?.toString?.() || obj?.title || ""; }
  _emit(name, detail) { window.dispatchEvent(new CustomEvent(name, { detail })); }
}
